import { useState, useEffect } from 'react';
import { Check, Flame, Wind, HelpCircle } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { BunsenBurner, Crucible } from '@/components/lab';

const stepInfoContent: Record<number, { title: string; content: string }> = {
  1: {
    title: 'Gentle Heating',
    content: 'Use a LOW flame to prevent splattering. Rapid heating causes the liquid to boil violently, ejecting droplets of your sample and ruining your mass measurements. Patience here ensures accurate results.'
  },
  2: {
    title: 'Monitoring Evaporation',
    content: 'Watch for the brown NO2 fumes to diminish and stop. This indicates the excess nitric acid is evaporating. Only increase heat after fumes stop to avoid decomposing your product before the acid is gone.'
  }
};

export default function Part5Evaporation() {
  const [burnerLit, setBurnerLit] = useState(false);
  const [evaporationProgress, setEvaporationProgress] = useState(0);
  const [fumesActive, setFumesActive] = useState(true);
  const [evaporationComplete, setEvaporationComplete] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showInfo, setShowInfo] = useState<number | null>(null);

  // Evaporation progress
  useEffect(() => {
    if (burnerLit && evaporationProgress < 100) {
      const timer = setInterval(() => {
        setEvaporationProgress(prev => {
          const newProgress = Math.min(prev + 1, 100);
          if (newProgress > 70) {
            setFumesActive(false);
          }
          if (newProgress >= 100) {
            setEvaporationComplete(true);
          }
          return newProgress;
        });
      }, 50);
      return () => clearInterval(timer);
    }
  }, [burnerLit, evaporationProgress]);

  const handleStartHeating = () => {
    if (currentStep === 1) {
      setBurnerLit(true);
      setCurrentStep(2);
    }
  };

  const completedCount = [burnerLit, evaporationComplete].filter(Boolean).length;

  // Fume particles for visualization
  const fumeCount = fumesActive ? Math.floor((100 - evaporationProgress) / 15) : 0;

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <Wind size={16} />
          <h3>Part V: Evaporate Acid</h3>
        </div>

        <div className="warning-note">
          <Flame size={14} />
          <span>Use LOW flame only!</span>
        </div>

        <div className="steps-list">
          {[
            { id: 1, title: 'Heat Gently', hint: 'LOW flame - prevent splattering', done: burnerLit },
            { id: 2, title: 'Monitor Evaporation', hint: 'Until brown fumes stop', done: evaporationComplete }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {burnerLit && (
          <div className="progress-section">
            <h4>Evaporation Progress</h4>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${evaporationProgress}%` }} />
            </div>
            <span className="progress-text">{evaporationProgress}%</span>

            <div className="fumes-indicator">
              <Wind size={14} />
              <span>NO₂ Fumes: {fumesActive ? 'Active' : 'Stopped'}</span>
              <div className={`indicator-light ${fumesActive ? 'active' : 'inactive'}`} />
            </div>
          </div>
        )}
      </div>

      <LabBench
        experimentName="Evaporate Nitric Acid"
        completedCount={completedCount}
        totalSteps={2}
        equipment={['Bunsen burner (low flame)', 'Fume hood', 'Crucible']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="evaporation-workspace">
          <div className="hood-indicator">
            <Wind size={16} />
            <span>In Fume Hood</span>
          </div>

          <div className="equipment-setup">
            {/* Retort Stand with Crucible */}
            <div className="ring-stand">
              <svg viewBox="0 0 120 200" width={120} height={200}>
                {/* Stand base */}
                <rect x="20" y="190" width="80" height="8" fill="#444" rx="2" />
                {/* Vertical rod */}
                <rect x="56" y="30" width="8" height="160" fill="#666" />
                {/* Iron ring */}
                <ellipse cx="60" cy="80" rx="35" ry="8" fill="none" stroke="#888" strokeWidth="4" />
                {/* Clay triangle */}
                <polygon points="35,80 85,80 60,70" fill="#B8860B" stroke="#8B6914" strokeWidth="1" />

                {/* Crucible */}
                <path
                  d="M40 65 L40 50 Q40 35 60 35 Q80 35 80 50 L80 65 Q80 72 60 72 Q40 72 40 65"
                  fill="#D3D3D3"
                  stroke="#888"
                  strokeWidth="2"
                />

                {/* Contents - yellowish residue */}
                <ellipse
                  cx="60"
                  cy="58"
                  rx="15"
                  ry="6"
                  fill={evaporationProgress < 50 ? '#FFFFE0' : evaporationProgress < 80 ? '#F5F5DC' : '#FFFEF5'}
                />

                {/* Brown fumes */}
                {Array.from({ length: fumeCount }).map((_, i) => (
                  <g key={i}>
                    <circle
                      cx={50 + Math.random() * 20}
                      cy={35}
                      r={2 + Math.random() * 2}
                      fill="#8B4513"
                      opacity={0.5}
                      className="fume-particle"
                      style={{ animationDelay: `${i * 0.3}s` }}
                    />
                  </g>
                ))}
              </svg>
            </div>

            {/* Bunsen Burner */}
            <div
              className={`burner-station ${currentStep === 1 ? 'clickable' : ''}`}
              onClick={handleStartHeating}
            >
              <BunsenBurner
                isLit={burnerLit}
                flameIntensity={burnerLit ? 30 : 0}
              />
              <span className="equipment-label">Bunsen Burner (LOW)</span>
            </div>
          </div>

          {evaporationComplete && (
            <div className="status-message success">
              <Check size={20} />
              Acid evaporated. Ready for strong heating to form oxide.
            </div>
          )}

          <div className="info-box">
            <h4>Why Gentle Heat?</h4>
            <p>
              Strong heat before the acid evaporates causes splattering and loss of sample.
              Watch for the brown NO₂ fumes to diminish before increasing heat.
            </p>
          </div>
        </div>

        <style>{`
          .evaporation-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 20px;
            padding: 20px;
            width: 100%;
          }

          .warning-note {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 8px 12px;
            background: rgba(255, 165, 0, 0.2);
            border-radius: 6px;
            color: #FFA500;
            font-size: 11px;
            font-weight: 600;
            margin-bottom: 8px;
          }

          .hood-indicator {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 8px 16px;
            background: rgba(0, 168, 107, 0.2);
            border-radius: 8px;
            color: var(--secondary);
            font-size: 12px;
            font-weight: 600;
          }

          .equipment-setup {
            display: flex;
            gap: 20px;
            align-items: flex-end;
          }

          .ring-stand {
            display: flex;
            flex-direction: column;
            align-items: center;
          }

          .burner-station {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 10px;
            border-radius: 8px;
            transition: all 0.2s;
          }

          .clickable {
            cursor: pointer;
            animation: pulse 1.5s ease-in-out infinite;
          }

          .clickable:hover {
            background: rgba(0, 168, 107, 0.1);
          }

          @keyframes pulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(255, 90, 54, 0.4); }
            50% { box-shadow: 0 0 0 8px rgba(255, 90, 54, 0); }
          }

          .equipment-label {
            font-size: 11px;
            color: var(--text-secondary);
          }

          .fume-particle {
            animation: rise 2s ease-out infinite;
          }

          @keyframes rise {
            0% { transform: translateY(0); opacity: 0.6; }
            100% { transform: translateY(-40px); opacity: 0; }
          }

          .progress-section {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 12px;
            margin-top: 12px;
          }

          .progress-section h4 {
            margin: 0 0 8px 0;
            font-size: 12px;
            color: var(--text-secondary);
          }

          .progress-bar {
            height: 8px;
            background: var(--border);
            border-radius: 4px;
            overflow: hidden;
            margin-bottom: 4px;
          }

          .progress-fill {
            height: 100%;
            background: linear-gradient(90deg, #8B4513, #FFA500);
            transition: width 0.1s;
          }

          .progress-text {
            font-size: 11px;
            color: var(--text-muted);
          }

          .fumes-indicator {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-top: 8px;
            font-size: 11px;
            color: var(--text-secondary);
          }

          .indicator-light {
            width: 8px;
            height: 8px;
            border-radius: 50%;
          }

          .indicator-light.active {
            background: #8B4513;
            box-shadow: 0 0 6px #8B4513;
          }

          .indicator-light.inactive {
            background: #4ADE80;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-weight: 500;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .info-box {
            background: var(--bg-elevated);
            border-left: 3px solid var(--secondary);
            border-radius: 8px;
            padding: 16px;
            max-width: 400px;
          }

          .info-box h4 {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--secondary);
          }

          .info-box p {
            margin: 0;
            font-size: 12px;
            color: var(--text-secondary);
            line-height: 1.5;
          }
          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>
      </LabBench>

      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp11"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
